import { ChangeEvent, FormEvent } from 'react';
import Modal from 'react-modal';
import { Option } from '../../src/utils/option';
import styled from 'styled-components';
import i18n from '../R/string/i18n';
import color from '../utils/style/color';
import { fontWeight, fontSize } from '../utils/style/font';
import space from '../utils/style/space';
import { FieldType } from '../presenters/me/userProfileHooks';
import ProfileEditModalBackground from '../atoms/ProfileEditModalBackground';
import { Nullable } from '../utils/type';
import assets from '../R/assets';
import Image from '../../src/atoms/Image';
import Loader from '../organisms/Loader';
import router from 'next/router';
import { getProfileFieldTitle } from '../utils/userProfile';
import UpdateUserNameForm from '../organisms/UpdateUserNameForm';
import UpdateLocationForm from '../organisms/UpdateUserLocationForm';
import UpdateNicknameForm from '../organisms/UpdateNicknameForm';
import useUpdateUser from '../presenters/me/useUpdateUser';
import { SecureUserProfile, UserProfile } from '../gateways/api/models';
import {
  Button,
  ButtonContainer,
  FormWrapper,
  Paragraph
} from '../atoms/EditFormContent';
import ButtonLabel from '../atoms/ButtonLabel';
import { UserData } from '../../server/session';
import notificationApi from '../../src/gateways/api/notificationApi';
import { SIGNUP_TYPE } from '../domain/signup';

type Props = {
  userData: UserData;
  fieldType: Nullable<FieldType>;
  content?: JSX.Element;
  onCloseEditModal: () => void;
  onUpdateUserProfile: (userProfile: SecureUserProfile) => void;
  onResetEmail?: (email: string) => void;
  accountSettingModalOpen?: boolean;
  needReceiveEmail?: boolean;
  toggleReceiveEmail?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRedirect?: () => void;
  onRedirectTo?: (signupType: SIGNUP_TYPE) => void;
  shouldRedirectTo?: boolean;
};

const BodyWrapper = styled.div`
  position: relative;
`;

const Title = styled.h1`
  font-size: ${fontSize.xxl}px;
  font-weight: ${fontWeight.normal};
  line-height: 1.5;
  color: ${color.gray.xxDark};
  padding-bottom: ${space.m}px;
  margin-bottom: ${space.m}px;
  border-bottom: 1px solid ${color.border.gray};
`;

const Title2 = styled.h1`
  font-size: ${fontSize.xxl}px;
  font-weight: ${fontWeight.xBold};
  line-height: 1.5;
  color: ${color.gray.xxDark};
  padding-bottom: ${space.m}px;
  text-align: center;
`;

const Description = styled.p`
  font-size: ${fontSize.m}px;
  font-weight: ${fontWeight.normal};
  line-height: 1.5;
  color: ${color.gray.xxDark};
  text-align: center;
`;

const CloseButtonIcon = styled.button`
  background: none;
  position: absolute;
  right: -18px;
  top: -18px;
  cursor: pointer;

  @media screen and (max-width: 576px) {
    right: 0;
    top: 0;
  }
`;

const MailContainer = styled.div`
  margin: ${space.xl}px 0 ${space.xxs}px;
`;

const Label = styled.div`
  font-weight: bold;
  padding-bottom: ${space.xxs}px;
`;

const Text = styled.div`
  word-break: break-word;
`;

const ProfileEditModal = (props: Props) => {
  const { user } = props.userData;
  const { email } = user.secure_user || '';
  const {
    needReceiveEmail = true,
    accountSettingModalOpen,
    onRedirect,
    onRedirectTo,
    shouldRedirectTo
  } = props;

  const {
    isUpdateLoading,
    firstName,
    lastName,
    location,
    nickname,
    onChangeFirstName,
    onChangeLastName,
    onChangeLocation,
    onChangeNickname,
    firstNameErr,
    lastNameErr,
    locationErr,
    nicknameErr,
    serverErrMsg,
    locationServerErrMsg,
    updateNameEnabled,
    updateLocationEnabled,
    updateNicknameEnabled,
    userUpdatePresenter
  } = useUpdateUser(Option(router));

  const onUpdateUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return;

    const secureUserProfile = user.secure_user_profile;
    // locationが既に存在している場合、再度設定しないようにする
    const updatedFirstName = firstName || secureUserProfile?.first_name || '';
    const updatedLastName = lastName || secureUserProfile?.last_name || '';
    const updatedLocation = location || secureUserProfile?.location || '';
    const updatedNickname = nickname || secureUserProfile?.nickname || '';

    const updatedUserProfile = {
      firstName: updatedFirstName,
      lastName: updatedLastName,
      location: updatedLocation,
      nickname: updatedNickname
    };
    const result = await userUpdatePresenter.onUpdate(
      secureUserProfile,
      updatedUserProfile
    );

    if (result && result.secure_user_profile) {
      const { secure_user_profile } = result as UserProfile;
      if (secure_user_profile) {
        closeEditModal();
        props.onUpdateUserProfile(secure_user_profile);
      }
      // SNS会員登録時のアカウント設定モーダルの時のみ、メール受信設定を更新する
      if (accountSettingModalOpen) {
        notificationApi.updateNotifications(user.id, [
          {
            permission_id: 'wamazing_mail',
            permitted: needReceiveEmail
          }
        ]);
        if (shouldRedirectTo) {
          onRedirectTo && onRedirectTo(SIGNUP_TYPE.FACEBOOK);
        } else {
          onRedirect && onRedirect();
        }
      }
    } else {
      console.log('Result or secure_user_profile is undefined', result);
      return;
    }
  };

  const closeEditModal = () => {
    props.onCloseEditModal();
  };

  const customModalStyle = {
    overlay: {
      zIndex: 3,
      backgroundColor: 'rgba(0, 0, 0, 0.3)'
    },
    content: {
      backgroundColor: 'transparent',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      border: 'none',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)'
    }
  };

  return (
    <Modal
      isOpen={!!props.fieldType || accountSettingModalOpen === true}
      contentLabel="Edit Modal"
      style={customModalStyle}
    >
      <ProfileEditModalBackground>
        <BodyWrapper>
          {!accountSettingModalOpen && (
            <CloseButtonIcon onClick={closeEditModal}>
              <Image
                src={assets.closeIcon}
                alt={i18n.commonCloseButton()}
                width={18}
                height={18}
              />
            </CloseButtonIcon>
          )}
          {accountSettingModalOpen ? (
            <Title2>{i18n.userInfoTitle()}</Title2>
          ) : (
            <Title>{getProfileFieldTitle(props.fieldType)}</Title>
          )}
          {accountSettingModalOpen && (
            <Description>{i18n.registerLocationMerit()}</Description>
          )}
          {props.fieldType === FieldType.PASSWORD && (
            <FormWrapper>
              <div>
                <Paragraph>{i18n.resetPasswordTitleTwo()}</Paragraph>
              </div>
              <MailContainer>
                <Label>{i18n.commonEmail()}</Label>
                <Text>{email}</Text>
              </MailContainer>
              <ButtonContainer>
                <Button
                  onClick={() => {
                    props.onResetEmail && props.onResetEmail(email);
                    props.onCloseEditModal();
                  }}
                >
                  <ButtonLabel>{i18n.resetPasswordSendMeALink()}</ButtonLabel>
                </Button>
              </ButtonContainer>
            </FormWrapper>
          )}
          {props.fieldType === FieldType.NAME && (
            <UpdateUserNameForm
              firstName={firstName}
              lastName={lastName}
              onChangeFirstName={onChangeFirstName}
              onChangeLastName={onChangeLastName}
              firstNameErr={firstNameErr}
              lastNameErr={lastNameErr}
              serverErrMsg={serverErrMsg}
              updateEnabled={updateNameEnabled}
              onUpdateName={onUpdateUser}
            />
          )}
          {(props.fieldType === FieldType.LOCATION ||
            accountSettingModalOpen) && (
            <UpdateLocationForm
              location={location}
              onChangeLocation={onChangeLocation}
              locationErr={locationErr}
              serverErrMsg={locationServerErrMsg}
              updateEnabled={updateLocationEnabled}
              onUpdateLocation={onUpdateUser}
              accountSettingModalOpen={accountSettingModalOpen}
              needReceiveEmail={props.needReceiveEmail}
              toggleReceiveEmail={props.toggleReceiveEmail}
            />
          )}
          {props.fieldType === FieldType.NICKNAME && (
            <UpdateNicknameForm
              nickname={nickname}
              onChangeNickname={onChangeNickname}
              nicknameErr={nicknameErr}
              serverErrMsg={serverErrMsg}
              updateEnabled={updateNicknameEnabled}
              onUpdateNickname={onUpdateUser}
            />
          )}
        </BodyWrapper>
      </ProfileEditModalBackground>
      <Loader isLoading={isUpdateLoading} />
    </Modal>
  );
};

export default ProfileEditModal;
